import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LandingPage.css';
import '../styles/LandingPageTheme.css';
import ROICalculator from '../components/common/ROICalculator';

function LandingPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    const [highContrast, setHighContrast] = useState(() => localStorage.getItem('highContrast') === 'true');

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        document.documentElement.setAttribute('data-contrast', highContrast ? 'high' : 'normal');
        localStorage.setItem('theme', theme);
        localStorage.setItem('highContrast', highContrast);
    }, [theme, highContrast]);

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    const toggleContrast = () => {
        setHighContrast(prev => !prev);
    };

    return (
        <div className="landing-page">
            {/* Navbar */}
            <nav className="landing-navbar">
                <div className="landing-navbar-container">
                    <Link to="/" className="landing-logo">
                        <div className="logo-icon">
                            <img src="/images/logo.svg" alt="MicroGestor Logo" width="32" height="32" />
                        </div>
                        <span className="logo-text">MicroGestor</span>
                    </Link>

                    <div className={`landing-nav-links ${mobileMenuOpen ? 'active' : ''}`}>
                        <a href="#solucao" onClick={() => setMobileMenuOpen(false)}>Solução</a>
                        <a href="#recursos" onClick={() => setMobileMenuOpen(false)}>Recursos</a>
                        <Link to="/planos" onClick={() => setMobileMenuOpen(false)}>Planos</Link>
                    </div>

                    <div className="landing-nav-actions">
                        <button
                            onClick={toggleTheme}
                            className="btn-accessibility"
                            aria-label={`Mudar para modo ${theme === 'light' ? 'escuro' : 'claro'}`}
                            title={`Modo ${theme === 'light' ? 'escuro' : 'claro'}`}
                        >
                            <i className={`bi ${theme === 'light' ? 'bi-moon-stars' : 'bi-sun'}`}></i>
                        </button>
                        <button
                            onClick={toggleContrast}
                            className="btn-accessibility"
                            aria-label={`${highContrast ? 'Desativar' : 'Ativar'} alto contraste`}
                            title="Alto contraste"
                        >
                            <i className="bi bi-circle-half"></i>
                        </button>
                        <Link to="/login" className="btn-login">Login</Link>
                        <Link to="/registro" className="btn-register">Começar Grátis</Link>
                    </div>

                    <button
                        className="mobile-menu-btn"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <i className={`bi ${mobileMenuOpen ? 'bi-x' : 'bi-list'}`}></i>
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero-section" id="home">
                <div className="hero-content">
                    <h1 className="hero-title" style={{ fontSize: 'clamp(2rem, 5vw, 3rem)' }}>
                        Gerencie seu negócio de forma simples e eficiente
                    </h1>
                    <p className="hero-subtitle">
                        Você perde tempo com planilhas confusas? Não sabe quanto está lucrando?
                        O MicroGestor é a solução completa para microempreendedores que querem
                        ter controle total do seu negócio.
                    </p>
                    <div className="hero-cta">
                        <Link to="/registro" className="btn btn-primary btn-lg">
                            Comece Grátis Agora
                        </Link>
                        <Link to="/planos" className="btn btn-outline-primary btn-lg">
                            Ver Planos
                        </Link>
                    </div>
                    <div className="hero-stats">
                        <div className="stat-item">
                            <div className="stat-icon">
                                <i className="bi bi-star-fill"></i>
                            </div>
                            <div className="stat-info">
                                <span className="stat-number">5.0</span>
                                <span className="stat-label">Avaliação</span>
                            </div>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <div className="stat-icon">
                                <i className="bi bi-shield-check"></i>
                            </div>
                            <div className="stat-info">
                                <span className="stat-number">100%</span>
                                <span className="stat-label">Seguro e Confiável</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="hero-visual">
                    {/* Floating Cards Container */}
                    <div className="floating-cards-container">
                        {/* Main Dashboard Card */}
                        <div className="floating-card main-card">
                            <div className="card-header">
                                <i className="bi bi-graph-up"></i>
                                <span>Vendas do Mês</span>
                            </div>
                            <div className="card-content">
                                <div className="metric-value">R$ 24.850</div>
                                <div className="metric-change positive">
                                    <i className="bi bi-arrow-up"></i>
                                    <span>+18% vs mês anterior</span>
                                </div>
                                <div className="mini-chart">
                                    <div className="chart-bar" style={{ height: '40%' }}></div>
                                    <div className="chart-bar" style={{ height: '60%' }}></div>
                                    <div className="chart-bar" style={{ height: '45%' }}></div>
                                    <div className="chart-bar" style={{ height: '80%' }}></div>
                                    <div className="chart-bar" style={{ height: '65%' }}></div>
                                    <div className="chart-bar" style={{ height: '90%' }}></div>
                                    <div className="chart-bar" style={{ height: '100%' }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Products Card */}
                        <div className="floating-card products-card">
                            <div className="card-header-mini">
                                <i className="bi bi-box-seam"></i>
                                <span>Produtos</span>
                            </div>
                            <div className="metric-large">156</div>
                            <div className="metric-sublabel">cadastrados</div>
                        </div>

                        {/* Profit Card */}
                        <div className="floating-card profit-card">
                            <div className="card-header-mini">
                                <i className="bi bi-cash-coin"></i>
                                <span>Lucro</span>
                            </div>
                            <div className="metric-large">R$ 8.2k</div>
                            <div className="metric-change positive">
                                <i className="bi bi-arrow-up"></i>
                                <span>+22%</span>
                            </div>
                        </div>

                        {/* Orders Card */}
                        <div className="floating-card orders-card">
                            <div className="card-header-mini">
                                <i className="bi bi-receipt"></i>
                                <span>Pedidos Hoje</span>
                            </div>
                            <div className="metric-large">42</div>
                            <div className="orders-list">
                                <div className="order-item"></div>
                                <div className="order-item"></div>
                                <div className="order-item"></div>
                            </div>
                        </div>

                        {/* Notification Badge */}
                        <div className="floating-badge notification-badge">
                            <i className="bi bi-bell-fill"></i>
                            <span>3 novos pedidos</span>
                        </div>

                        {/* Success Badge */}
                        <div className="floating-badge success-badge">
                            <i className="bi bi-check-circle-fill"></i>
                            <span>Tudo em dia!</span>
                        </div>

                        {/* Decorative Elements */}
                        <div className="decorative-circle circle-1"></div>
                        <div className="decorative-circle circle-2"></div>
                        <div className="decorative-dot dot-1"></div>
                        <div className="decorative-dot dot-2"></div>
                        <div className="decorative-dot dot-3"></div>
                    </div>
                </div>
            </section>

            {/* Problema Section */}
            <section className="problem-section">
                <div className="container">
                    <h2 className="section-title">Você enfrenta esses desafios?</h2>
                    <div className="problems-grid">
                        <div className="problem-card">
                            <i className="bi bi-file-spreadsheet problem-icon"></i>
                            <h3>Planilhas Confusas</h3>
                            <p>Passa horas tentando entender suas planilhas e ainda assim não tem certeza dos números?</p>
                        </div>
                        <div className="problem-card">
                            <i className="bi bi-cash-stack problem-icon"></i>
                            <h3>Não Sabe o Lucro Real</h3>
                            <p>Vende muito mas não sabe se está realmente lucrando ou apenas girando dinheiro?</p>
                        </div>
                        <div className="problem-card">
                            <i className="bi bi-clock-history problem-icon"></i>
                            <h3>Perde Tempo</h3>
                            <p>Gasta tempo precioso com tarefas administrativas ao invés de focar no seu negócio?</p>
                        </div>
                        <div className="problem-card">
                            <i className="bi bi-exclamation-triangle problem-icon"></i>
                            <h3>Falta de Controle</h3>
                            <p>Não tem visibilidade clara de estoque, pedidos e despesas em um só lugar?</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Solução Section */}
            <section className="solution-section" id="solucao">
                <div className="container">
                    <h2 className="section-title">Como o MicroGestor Resolve</h2>
                    <div className="row align-items-center g-5">
                        <div className="col-lg-6">
                            <div className="solution-content">
                                <div className="solution-item">
                                    <div className="solution-icon">
                                        <i className="bi bi-speedometer2"></i>
                                    </div>
                                    <div className="solution-text">
                                        <h3>Dashboard Inteligente</h3>
                                        <p>Veja todos os números importantes do seu negócio em tempo real, de forma visual e fácil de entender.</p>
                                    </div>
                                </div>
                                <div className="solution-item">
                                    <div className="solution-icon">
                                        <i className="bi bi-calculator"></i>
                                    </div>
                                    <div className="solution-text">
                                        <h3>Cálculos Automáticos</h3>
                                        <p>Lucro, margem, estoque - tudo calculado automaticamente. Você só precisa inserir as informações.</p>
                                    </div>
                                </div>
                                <div className="solution-item">
                                    <div className="solution-icon">
                                        <i className="bi bi-phone"></i>
                                    </div>
                                    <div className="solution-text">
                                        <h3>Acesso de Qualquer Lugar</h3>
                                        <p>Gerencie seu negócio do celular, tablet ou computador. Seus dados sempre sincronizados.</p>
                                    </div>
                                </div>
                                <div className="solution-item">
                                    <div className="solution-icon">
                                        <i className="bi bi-shield-check"></i>
                                    </div>
                                    <div className="solution-text">
                                        <h3>Dados Seguros</h3>
                                        <p>Seus dados protegidos na nuvem com a segurança do Google Firebase.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="solution-image">
                                <div className="dashboard-preview" style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '16px',
                                    padding: '2rem',
                                    boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)',
                                    position: 'relative'
                                }}>
                                    <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h4 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>Dashboard</h4>
                                            <i className="bi bi-graph-up-arrow" style={{ fontSize: '1.5rem', color: '#667eea' }}></i>
                                        </div>
                                        <div className="row g-2 mb-3">
                                            <div className="col-6">
                                                <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '0.75rem', borderRadius: '8px', color: 'white' }}>
                                                    <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>Receita</div>
                                                    <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>R$ 12.500</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '0.75rem', borderRadius: '8px', color: 'white' }}>
                                                    <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>Lucro</div>
                                                    <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>R$ 4.300</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ background: '#f3f4f6', padding: '1rem', borderRadius: '8px' }}>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Vendas</span>
                                                <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>156</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Produtos</span>
                                                <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>45</span>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Clientes</span>
                                                <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>89</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        position: 'absolute',
                                        top: '-10px',
                                        right: '-10px',
                                        width: '40px',
                                        height: '40px',
                                        background: '#10b981',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white'
                                    }}>
                                        <i className="bi bi-check-lg" style={{ fontSize: '1.5rem' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Para Quem Section */}
            <section className="target-section">
                <div className="container">
                    <h2 className="section-title">Para Quem é o MicroGestor?</h2>
                    <div className="target-grid">
                        <div className="target-card">
                            <i className="bi bi-shop target-icon"></i>
                            <h3>Pequenos Comerciantes</h3>
                            <p>Lojas físicas ou online que precisam controlar estoque, vendas e lucro.</p>
                        </div>
                        <div className="target-card">
                            <i className="bi bi-person-badge target-icon"></i>
                            <h3>Prestadores de Serviço</h3>
                            <p>Profissionais autônomos que querem organizar seus atendimentos e finanças.</p>
                        </div>
                        <div className="target-card">
                            <i className="bi bi-cart target-icon"></i>
                            <h3>Revendedores</h3>
                            <p>Quem trabalha com revenda e precisa calcular margem de lucro rapidamente.</p>
                        </div>
                        <div className="target-card">
                            <i className="bi bi-lightbulb target-icon"></i>
                            <h3>Empreendedores Iniciantes</h3>
                            <p>Quem está começando e quer desde o início ter organização e controle.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section" id="recursos">
                <div className="container">
                    <h2 className="section-title">Recursos Principais</h2>
                    <div className="features-grid">
                        <div className="feature-card">
                            <i className="bi bi-box-seam feature-icon"></i>
                            <h3>Gestão de Produtos</h3>
                            <p>Cadastre produtos com custo, preço de venda e estoque. Acompanhe tudo em tempo real.</p>
                        </div>
                        <div className="feature-card">
                            <i className="bi bi-graph-up feature-icon"></i>
                            <h3>Controle de Vendas</h3>
                            <p>Registre vendas e veja automaticamente seu lucro e margem de cada transação.</p>
                        </div>
                        <div className="feature-card">
                            <i className="bi bi-receipt feature-icon"></i>
                            <h3>Controle de Despesas</h3>
                            <p>Organize todas as suas despesas por categoria e saiba exatamente para onde vai seu dinheiro.</p>
                        </div>
                        <div className="feature-card">
                            <i className="bi bi-people feature-icon"></i>
                            <h3>Cadastro de Clientes</h3>
                            <p>Mantenha os dados dos seus clientes organizados e acessíveis.</p>
                        </div>
                        <div className="feature-card">
                            <i className="bi bi-clipboard-check feature-icon"></i>
                            <h3>Gestão de Pedidos</h3>
                            <p>Crie pedidos, acompanhe status e envie links de acompanhamento aos clientes.</p>
                        </div>
                        <div className="feature-card">
                            <i className="bi bi-file-earmark-bar-graph feature-icon"></i>
                            <h3>Relatórios Completos</h3>
                            <p>Gráficos e relatórios que mostram a saúde do seu negócio de forma visual.</p>
                        </div>
                        <div className="feature-card">
                            <i className="bi bi-pie-chart feature-icon"></i>
                            <h3>Análise de Lucratividade</h3>
                            <p>Descubra quais produtos dão mais lucro e quais estão prejudicando seu negócio.</p>
                        </div>
                        <div className="feature-card">
                            <i className="bi bi-download feature-icon"></i>
                            <h3>Exportação de Dados</h3>
                            <p>Exporte seus dados para Excel/CSV quando precisar.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ROI Calculator Section */}
            <ROICalculator />

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-7">
                            <h2 className="cta-title">Pronto para transformar a gestão do seu negócio?</h2>
                            <p className="cta-subtitle" style={{ color: 'white' }}>Comece gratuitamente hoje mesmo. Não precisa cartão de crédito.</p>
                            <Link to="/registro" className="btn btn-primary btn-lg" style={{ width: 'auto' }}>
                                Criar Conta Grátis
                            </Link>
                        </div>
                        <div className="col-lg-5">
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '16px',
                                padding: '2rem',
                                textAlign: 'center',
                                boxShadow: '0 20px 60px rgba(102, 126, 234, 0.2)'
                            }}>
                                <i className="bi bi-rocket-takeoff" style={{ fontSize: '5rem', color: 'white' }}></i>
                                <div style={{ color: 'white', marginTop: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
                                    Comece sua jornada hoje!
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <h3>MicroGestor</h3>
                            <p>Gestão simples para seu negócio crescer</p>
                        </div>
                        <div className="footer-links">
                            <Link to="/planos">Planos</Link>
                            <Link to="/login">Login</Link>
                            <Link to="/registro">Registrar</Link>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <p className="mb-0">&copy; 2025 MicroGestor. Todos os direitos reservados.</p>
                            <a
                                href="mailto:octaviofp2003@outlook.com"
                                className="btn btn-outline-light btn-sm btn-contact"
                                style={{ borderColor: 'rgba(255,255,255,0.3)', width: 'auto' }}
                                aria-label="Enviar email para entrar em contato"
                            >
                                <i className="bi bi-envelope me-2"></i>Fale conosco
                            </a>
                        </div>
                    </div>
                </div> 
            </footer>
        </div>
    );
}

export default LandingPage;
